// Manual image/video uploads (default-on brand-level setting
// `ads_express_manual_upload`, admin opt-out). Opened from My Ads: files
// are staged first, tags (persona / product / narrative) can be applied
// globally to the whole batch or overridden per image, then a bounded direct-R2
// queue uploads each file independently. Uploads become ready, saved
// `generated_ads` rows (R2-backed) with a "Manual upload" marker.
//
// Task #1716 — the tag dropdowns are the SAME rich PickerSelect Ads Express
// uses (thumbnails, descriptions, search, angles grouped by Opportunity
// family), and the angle options are scoped exactly like Ads Express:
// archived angles excluded, angles narrowed to the selected persona/product.
import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, ImagePlus, Loader2, Package, RotateCcw, Sparkles, Upload, Users, X, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PickerSelect, type PickerGroup, type PickerOption } from "@/components/picker-select";
import { groupAnglesByFamily } from "@/lib/angle-groups";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import type { Persona, Product, Offer, NarrativeAngle } from "@shared/schema";
import {
  MANUAL_UPLOAD_LIMITS,
  ManualUploadQueue,
  manualUploadSelectionAllowance,
  type ManualUploadState,
  type ManualUploadTags,
} from "./manual-upload-queue";

// Per-file tag override: undefined = inherit the global choice, "" = explicitly none, else an id.
type TagOverride = {
  personaId?: string;
  promotedItemType?: "product" | "offer";
  productId?: string;
  offerId?: string;
  angleId?: string;
};
type StagedFile = {
  key: string;
  file: File;
  previewUrl: string;
  override: TagOverride;
  showTags: boolean;
  state: ManualUploadState;
  transferredBytes: number;
  error: string | null;
};
type FailedRow = { key: string; name: string; error: string };

const GLOBAL = "__global__";
const NONE = "__none__";

export function ManualAdUpload({
  brandId,
  personas,
  products,
  offers = [],
  angles,
  disabled = false,
  onUploaded,
}: {
  brandId: string;
  personas: Persona[];
  products: Product[];
  offers?: Offer[];
  angles: NarrativeAngle[];
  disabled?: boolean;
  // Called once every accepted row has completed successfully.
  onUploaded: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [failed, setFailed] = useState<FailedRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Global tags applied to every staged file unless overridden per file.
  const [personaId, setPersonaId] = useState("");
  const [productId, setProductId] = useState("");
  const [offerId, setOfferId] = useState("");
  const [promotedItemType, setPromotedItemType] = useState<"product" | "offer">("product");
  const [angleId, setAngleId] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const queueRef = useRef<ManualUploadQueue | null>(null);
  const stagedRef = useRef<StagedFile[]>([]);
  const onUploadedRef = useRef(onUploaded);
  const completionNotifiedRef = useRef(false);
  const busy = disabled;
  onUploadedRef.current = onUploaded;

  useEffect(() => {
    const queue = new ManualUploadQueue(
      `/api/brands/${brandId}/ads-express/manual-uploads`,
      (key, update) => {
        setStaged((prev) =>
          prev.map((row) =>
            row.key === key
              ? {
                  ...row,
                  ...update,
                  transferredBytes:
                    update.state === "queued" && update.transferredBytes === 0
                      ? 0
                      : Math.max(row.transferredBytes, update.transferredBytes ?? row.transferredBytes),
                }
              : row,
          ),
        );
      },
      () => {
        void queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === "string" &&
            (query.queryKey[0] as string).startsWith(`/api/brands/${brandId}/generated-ads`),
        });
      },
    );
    queueRef.current = queue;
    return () => {
      queue.dispose();
      queueRef.current = null;
    };
  }, [brandId, queryClient]);

  useEffect(() => {
    stagedRef.current = staged;
    setUploading(staged.some((row) => ["preparing", "uploading", "verifying"].includes(row.state)));
    if (
      staged.length > 0 &&
      staged.every((row) => row.state === "complete") &&
      !completionNotifiedRef.current
    ) {
      completionNotifiedRef.current = true;
      onUploadedRef.current();
    }
  }, [staged]);

  useEffect(
    () => () => {
      stagedRef.current.forEach((row) => URL.revokeObjectURL(row.previewUrl));
    },
    [],
  );

  const stageFiles = (fileList: FileList | File[]) => {
    if (busy) return;
    const batch = Date.now();
    const files: File[] = [];
    const rejected: FailedRow[] = [];
    const available = manualUploadSelectionAllowance(staged.length, Array.from(fileList).length);
    Array.from(fileList).forEach((file, i) => {
      const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      const isImage =
        /^image\/(png|jpe?g|webp|gif|avif)$/i.test(file.type) &&
        [".png", ".jpg", ".jpeg", ".webp", ".gif", ".avif"].includes(ext);
      const isVideo =
        /^video\/(mp4|webm|quicktime)$/i.test(file.type) &&
        [".mp4", ".webm", ".mov"].includes(ext);
      const error = !isImage && !isVideo
        ? "Unsupported type. Use PNG, JPEG, WebP, GIF, AVIF, MP4, WebM, or MOV."
        : isImage && file.size > 25 * 1024 * 1024
          ? "Images must be 25 MB or smaller."
          : null;
      if (error) rejected.push({ key: `${batch}-rejected-${i}-${file.name}`, name: file.name, error });
      else if (files.length >= available) {
        rejected.push({
          key: `${batch}-rejected-${i}-${file.name}`,
          name: file.name,
          error: `A batch can contain at most ${MANUAL_UPLOAD_LIMITS.maxFiles} files.`,
        });
      } else files.push(file);
    });
    if (rejected.length > 0) {
      setFailed((prev) => [...prev, ...rejected]);
      toast({
        title: files.length > 0 ? "Some files weren't added" : "No supported files selected",
        description: "Check the per-file errors below.",
        variant: "destructive",
      });
    }
    if (files.length === 0) {
      return;
    }
    completionNotifiedRef.current = false;
    setStaged((prev) => [
      ...prev,
      ...files.map((f, i) => ({
        key: `${batch}-${i}-${f.name}`,
        file: f,
        previewUrl: URL.createObjectURL(f),
        override: {},
        showTags: false,
        state: "queued" as const,
        transferredBytes: 0,
        error: null,
      })),
    ]);
  };

  // Merge a per-file override patch; a key explicitly set to undefined is
  // REMOVED (back to "use global") — spreads would keep it present.
  const setOverride = (key: string, patch: TagOverride) =>
    setStaged((prev) =>
      prev.map((s) => {
        if (s.key !== key) return s;
        const next = { ...s.override } as Record<string, string | undefined>;
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined) delete next[k];
          else next[k] = v;
        }
        return { ...s, override: next };
      }),
    );

  const effTag = (s: StagedFile, field: keyof TagOverride, global: string) =>
    field in s.override ? s.override[field] ?? "" : global;

  // ── Ads Express-parity option lists ───────────────────────────────────────
  // Personas with avatar thumbnails, products sorted by name with the first
  // media photo as thumbnail — exactly what ads-express.tsx feeds PickerSelect.
  const personaOptions = useMemo<PickerOption[]>(
    () =>
      personas.map((p) => ({
        value: p.id,
        label: p.name,
        imageUrl: (p as { imageUrl?: string | null }).imageUrl ?? null,
      })),
    [personas],
  );
  const productOptions = useMemo<PickerOption[]>(
    () =>
      [...products]
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
        .map((p) => {
          const media = (p as { mediaJson?: string[] | null }).mediaJson;
          const first = Array.isArray(media) && media.length > 0 ? media[0] : null;
          return { value: p.id, label: p.name, imageUrl: first };
        }),
    [products],
  );
  const offerOptions = useMemo<PickerOption[]>(
    () =>
      [...offers]
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
        .map((o) => ({ value: o.id, label: o.name })),
    [offers],
  );

  // Angles scoped the way Ads Express (product mode) scopes them: archived
  // excluded; a chosen persona/product narrows to matching angles. Angles
  // generated for an OFFER carry offeringId (productId null), so they drop
  // out as soon as a product is picked — same as Ads Express.
  const visibleAngles = (pid: string, prodId: string, offId = "") =>
    angles.filter(
      (a) =>
        a.status !== "archived" &&
        (!pid || a.personaId === pid) &&
        (!prodId || a.productId === prodId) &&
        (!offId || a.offeringId === offId),
    );

  // Group by Opportunity family (with summaries), optionally prefixed with
  // sentinel entries ("Use global" for per-file pickers, "No narrative").
  const angleGroupsFor = (pid: string, prodId: string, offId: string, withGlobal: boolean): PickerGroup[] => {
    const sentinels: PickerOption[] = [
      ...(withGlobal ? [{ value: GLOBAL, label: "Use global" }] : []),
      { value: NONE, label: "No narrative" },
    ];
    return [
      { label: "Default", options: sentinels },
      ...groupAnglesByFamily(visibleAngles(pid, prodId, offId)).map((g) => ({
        label: g.label,
        options: g.angles.map((a) => ({
          value: a.id,
          label: a.name,
          description: a.summary ?? null,
        })),
      })),
    ];
  };

  // If persona/product changes make the chosen global angle invalid, clear it
  // so we never quietly submit a mismatched angleId (Ads Express behavior).
  useEffect(() => {
    if (angleId && !visibleAngles(personaId, productId, offerId).some((a) => a.id === angleId)) {
      setAngleId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [angleId, personaId, productId, offerId, angles]);

  const tagsFor = (row: StagedFile): ManualUploadTags => ({
    personaId: effTag(row, "personaId", personaId) || null,
    productId: effTag(row, "productId", productId) || null,
    offerId: effTag(row, "offerId", offerId) || null,
    angleId: effTag(row, "angleId", angleId) || null,
  });

  const handleUpload = () => {
    if (!brandId || disabled || !queueRef.current) return;
    const queued = staged.filter((row) => row.state === "queued");
    if (queued.length === 0) return;
    setUploading(true);
    queueRef.current.enqueue(queued.map((row) => ({ key: row.key, file: row.file, tags: tagsFor(row) })));
  };

  const aggregate = staged.reduce(
    (counts, row) => {
      if (row.state === "queued") counts.waiting += 1;
      else if (["preparing", "uploading", "verifying"].includes(row.state)) counts.active += 1;
      else if (row.state === "complete") counts.completed += 1;
      else if (row.state === "failed") counts.failed += 1;
      return counts;
    },
    { waiting: 0, active: 0, completed: 0, failed: 0 },
  );
  const totalBytes = staged.reduce((sum, row) => sum + row.file.size, 0);
  const transferredBytes = staged.reduce((sum, row) => sum + Math.min(row.file.size, row.transferredBytes), 0);
  const aggregatePercent = totalBytes ? Math.floor((transferredBytes / totalBytes) * 100) : 0;
  const formatBytes = (bytes: number) =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!busy && e.dataTransfer?.files?.length) stageFiles(e.dataTransfer.files);
        }}
        onClick={() => !busy && fileInputRef.current?.click()}
        role="button"
        aria-disabled={busy}
        className={[
          "rounded-lg border border-dashed px-4 py-3 flex items-center gap-3 transition-colors",
          busy
            ? "opacity-60 cursor-not-allowed"
            : dragOver
            ? "border-primary bg-primary/5 cursor-pointer"
            : "cursor-pointer hover:border-primary/60 hover:bg-primary/5",
        ].join(" ")}
        data-testid="dropzone-manual-upload"
      >
        <ImagePlus className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="min-w-0">
          <p className="text-xs font-medium">Add media</p>
          <p className="text-[11px] text-muted-foreground">
            Drag & drop or click to select images or videos — set tags below, then upload to My Ads.
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/avif,video/mp4,video/webm,video/quicktime,.mov"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) stageFiles(e.target.files);
            e.target.value = "";
          }}
          data-testid="input-manual-upload"
        />
      </div>

      {/* Global tags — applied to ALL staged files unless overridden per
          file. Labels only, no AI processing is triggered. */}
      <div>
        <p className="text-[11px] font-medium text-muted-foreground mb-1">Tags for all files</p>
        <div className="grid grid-cols-1 gap-2">
          <PickerSelect
            icon={<Users className="h-4 w-4" />}
            label="Persona"
            placeholder="No persona"
            value={personaId}
            onChange={setPersonaId}
            options={personaOptions}
            testId="select-manual-persona"
            searchable
            optional
            onClear={() => setPersonaId("")}
            disabled={busy || uploading}
          />
          <div
            className="flex w-fit items-center gap-1 justify-self-end rounded-full border border-border/70 bg-background/60 p-0.5 shadow-sm"
            data-testid="toggle-manual-promoted-item"
          >
            <Button
              type="button"
              size="sm"
              variant={promotedItemType === "product" ? "default" : "ghost"}
              className="h-7 gap-1 rounded-full px-3 text-[11px]"
              onClick={() => {
                setPromotedItemType("product");
                setOfferId("");
                setAngleId("");
              }}
              disabled={busy || uploading}
              data-testid="manual-mode-product"
            >
              <Package className="h-3 w-3" /> Product
            </Button>
            <Button
              type="button"
              size="sm"
              variant={promotedItemType === "offer" ? "default" : "ghost"}
              className="h-7 gap-1 rounded-full px-3 text-[11px]"
              onClick={() => {
                setPromotedItemType("offer");
                setProductId("");
                setAngleId("");
              }}
              disabled={busy || uploading}
              data-testid="manual-mode-offer"
            >
              <Sparkles className="h-3 w-3" /> Offer
            </Button>
          </div>
          {promotedItemType === "offer" ? <PickerSelect
            icon={<Package className="h-4 w-4" />}
            label="Offer"
            placeholder="No offer"
            value={offerId}
            onChange={setOfferId}
            options={offerOptions}
            testId="select-manual-offer"
            searchable optional onClear={() => setOfferId("")}
            disabled={busy || uploading}
          /> : <PickerSelect
            icon={<Package className="h-4 w-4" />}
            label="Product"
            placeholder="No product"
            value={productId}
            onChange={(v) => {
              setProductId(v);
              // Keep the global angle consistent with the product filter.
              if (v && angleId) {
                const a = angles.find((x) => x.id === angleId);
                if (a && a.productId !== v) setAngleId("");
              }
            }}
            options={productOptions}
            testId="select-manual-product"
            searchable
            optional
            onClear={() => setProductId("")}
            disabled={busy || uploading}
          />}
          <PickerSelect
            icon={<Sparkles className="h-4 w-4" />}
            label="Narrative Angle"
            placeholder={
              visibleAngles(personaId, productId, offerId).length === 0
                ? "No saved angles for this selection"
                : "No narrative"
            }
            value={angleId}
            onChange={setAngleId}
            groups={angleGroupsFor(personaId, productId, offerId, false).slice(1)}
            testId="select-manual-angle"
            searchable
            wrapDescription
            optional
            onClear={() => setAngleId("")}
            disabled={busy || uploading || visibleAngles(personaId, productId, offerId).length === 0}
          />
        </div>
      </div>

      {staged.length > 0 && (
        <div className="space-y-2" data-testid="list-staged-uploads">
          <div className="rounded-md bg-muted/50 px-2.5 py-2 text-[11px] space-y-1" data-testid="manual-upload-summary">
            <div className="flex justify-between gap-2">
              <span>
                {aggregate.waiting} waiting · {aggregate.active} active · {aggregate.completed} completed · {aggregate.failed} failed
              </span>
              <span className="font-medium">{aggregatePercent}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary transition-[width]" style={{ width: `${aggregatePercent}%` }} />
            </div>
            <p className="text-muted-foreground">
              {formatBytes(transferredBytes)} of {formatBytes(totalBytes)}
            </p>
          </div>
          <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
          {staged.map((s) => {
            const ovPersona = "personaId" in s.override;
            const ovProduct = "productId" in s.override;
            const ovOffer = "offerId" in s.override;
            const ovPromotedItemType = "promotedItemType" in s.override;
            const ovAngle = "angleId" in s.override;
            const hasOverride = ovPersona || ovPromotedItemType || ovProduct || ovOffer || ovAngle;
            const effPersona = effTag(s, "personaId", personaId);
            const effProduct = effTag(s, "productId", productId);
            const effOffer = effTag(s, "offerId", offerId);
            const fileMode = s.override.promotedItemType ?? (effOffer ? "offer" : "product");
            return (
              <div key={s.key} className="rounded-md border px-2.5 py-2 space-y-2" data-testid={`staged-row-${s.key}`}>
                <div className="flex items-center gap-2 text-xs">
                  {s.file.type.startsWith("video/") ? (
                    <video
                      src={s.previewUrl}
                      muted
                      playsInline
                      preload="metadata"
                      className="h-9 w-9 rounded object-cover border shrink-0"
                    />
                  ) : (
                    <img
                      src={s.previewUrl}
                      alt=""
                      className="h-9 w-9 rounded object-cover border shrink-0"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{s.file.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      <span className="capitalize">{s.state}</span>
                      {" · "}
                      {formatBytes(s.transferredBytes)} of {formatBytes(s.file.size)}
                      {" · "}
                      {s.file.size ? Math.floor((Math.min(s.file.size, s.transferredBytes) / s.file.size) * 100) : 100}%
                    </p>
                    {s.error && <p className="text-[11px] text-destructive">{s.error}</p>}
                  </div>
                  {s.state === "queued" && !uploading && (
                    <button
                      type="button"
                      className="text-[11px] text-primary hover:underline shrink-0"
                      onClick={() =>
                        setStaged((prev) =>
                          prev.map((x) =>
                            x.key === s.key
                              ? x.showTags || hasOverride
                                ? { ...x, showTags: false, override: {} }
                                : { ...x, showTags: true }
                              : x,
                          ),
                        )
                      }
                      disabled={busy}
                      data-testid={`button-own-tags-${s.key}`}
                    >
                      {s.showTags || hasOverride ? "Use global tags" : "Edit tags"}
                    </button>
                  )}
                  {(["preparing", "uploading", "verifying"].includes(s.state) || (s.state === "queued" && uploading)) && (
                    <button
                      type="button"
                      className="text-[11px] text-destructive hover:underline shrink-0"
                      onClick={() => void queueRef.current?.cancel(s.key)}
                      aria-label={`Cancel ${s.file.name}`}
                    >
                      Cancel
                    </button>
                  )}
                  {(s.state === "failed" || s.state === "cancelled") && (
                    <button
                      type="button"
                      className="text-primary hover:text-primary/80 shrink-0"
                      onClick={() => queueRef.current?.retry({ key: s.key, file: s.file, tags: tagsFor(s) })}
                      aria-label={`Retry ${s.file.name}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {s.state === "complete" && <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />}
                  {s.state === "queued" && !uploading && (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground shrink-0"
                      onClick={() => {
                        URL.revokeObjectURL(s.previewUrl);
                        setStaged((prev) => prev.filter((x) => x.key !== s.key));
                      }}
                      disabled={busy}
                      aria-label={`Remove ${s.file.name}`}
                      data-testid={`button-remove-staged-${s.key}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {(s.showTags || hasOverride) && (
                  <div className="grid grid-cols-1 gap-2">
                    <PickerSelect
                      icon={<Users className="h-4 w-4" />}
                      label="Persona"
                      placeholder="Use global"
                      value={ovPersona ? (s.override.personaId || NONE) : GLOBAL}
                      onChange={(v) =>
                        setOverride(
                          s.key,
                          v === GLOBAL
                            ? ({ personaId: undefined } as TagOverride)
                            : { personaId: v === NONE ? "" : v, angleId: "" },
                        )
                      }
                      options={[
                        { value: GLOBAL, label: "Use global" },
                        { value: NONE, label: "No persona" },
                        ...personaOptions,
                      ]}
                      testId={`select-file-persona-${s.key}`}
                      searchable
                      optional
                       disabled={busy || s.state !== "queued" || uploading}
                    />
                    <div className="flex w-fit items-center gap-1 justify-self-end rounded-full border border-border/70 bg-background/60 p-0.5 shadow-sm">
                      <Button
                        type="button"
                        size="sm"
                        variant={fileMode === "product" ? "default" : "ghost"}
                        className="h-7 gap-1 rounded-full px-3 text-[11px]"
                        onClick={() => setOverride(s.key, {
                          promotedItemType: "product",
                          productId: undefined,
                          offerId: "",
                          angleId: "",
                        })}
                        disabled={busy || s.state !== "queued" || uploading}
                        data-testid={`file-mode-product-${s.key}`}
                      >
                        <Package className="h-3 w-3" /> Product
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={fileMode === "offer" ? "default" : "ghost"}
                        className="h-7 gap-1 rounded-full px-3 text-[11px]"
                        onClick={() => setOverride(s.key, {
                          promotedItemType: "offer",
                          productId: "",
                          offerId: undefined,
                          angleId: "",
                        })}
                        disabled={busy || s.state !== "queued" || uploading}
                        data-testid={`file-mode-offer-${s.key}`}
                      >
                        <Sparkles className="h-3 w-3" /> Offer
                      </Button>
                    </div>
                    {fileMode === "offer" ? <PickerSelect
                      icon={<Package className="h-4 w-4" />}
                      label="Offer"
                      placeholder="Use global"
                      value={ovOffer ? (s.override.offerId || NONE) : GLOBAL}
                      onChange={(v) => setOverride(s.key,
                        v === GLOBAL
                          ? ({ offerId: undefined } as TagOverride)
                          : { offerId: v === NONE ? "" : v, productId: "", angleId: "" })}
                      options={[
                        { value: GLOBAL, label: "Use global" },
                        { value: NONE, label: "No offer" },
                        ...offerOptions,
                      ]}
                      testId={`select-file-offer-${s.key}`}
                      searchable optional
                      disabled={busy || s.state !== "queued" || uploading}
                    /> : <PickerSelect
                      icon={<Package className="h-4 w-4" />}
                      label="Product"
                      placeholder="Use global"
                      value={ovProduct ? (s.override.productId || NONE) : GLOBAL}
                      onChange={(v) =>
                        setOverride(
                          s.key,
                          v === GLOBAL
                            ? ({ productId: undefined } as TagOverride)
                            : { productId: v === NONE ? "" : v, offerId: "", angleId: "" },
                        )
                      }
                      options={[
                        { value: GLOBAL, label: "Use global" },
                        { value: NONE, label: "No product" },
                        ...productOptions,
                      ]}
                      testId={`select-file-product-${s.key}`}
                      searchable
                      optional
                       disabled={busy || s.state !== "queued" || uploading}
                    />}
                    <PickerSelect
                      icon={<Sparkles className="h-4 w-4" />}
                      label="Narrative Angle"
                      placeholder="Use global"
                      value={ovAngle ? (s.override.angleId || NONE) : GLOBAL}
                      onChange={(v) =>
                        setOverride(
                          s.key,
                          v === GLOBAL
                            ? ({ angleId: undefined } as TagOverride)
                            : { angleId: v === NONE ? "" : v },
                        )
                      }
                      groups={angleGroupsFor(effPersona, effProduct, effOffer, true)}
                      testId={`select-file-angle-${s.key}`}
                      searchable
                      wrapDescription
                      optional
                       disabled={busy || s.state !== "queued" || uploading}
                    />
                  </div>
                )}
              </div>
            );
          })}
          </div>
          <Button
            size="sm"
            className="w-full"
            onClick={() => void handleUpload()}
            disabled={busy || uploading || aggregate.waiting === 0}
            data-testid="button-upload-staged"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
            {uploading ? "Uploading…" : `Upload ${aggregate.waiting} file${aggregate.waiting === 1 ? "" : "s"}`}
          </Button>
        </div>
      )}

      {failed.length > 0 && (
        <div className="space-y-1" data-testid="list-manual-uploads">
          {failed.map((u) => (
            <div key={u.key} className="flex items-center gap-2 text-[11px]" data-testid={`upload-row-${u.key}`}>
              <XCircle className="h-3 w-3 text-destructive shrink-0" />
              <span className="truncate">{u.name}</span>
              <span className="text-destructive truncate">{u.error || "Upload failed"}</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground shrink-0"
                onClick={() => setFailed((prev) => prev.filter((x) => x.key !== u.key))}
                aria-label={`Dismiss ${u.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
